@if (dialogMode) {
  <div class="mfa-dialog-header">
    <h2 class="mfa-dialog-title">
      @if (isManagingAnotherUser) {
        {{ "MFA.MANAGE_FOR_USER" | translate: { name: managedUserName } }}
      } @else {
        {{ "MFA.TITLE" | translate }}
      }
    </h2>
    <p class="mfa-dialog-description">
      {{ "MFA.MANAGE_DIALOG_DESCRIPTION" | translate }}
    </p>
  </div>

  <div class="mfa-guidance-card">
    <div class="mfa-guidance-copy">
      <h3>{{ "MFA.GUIDE_TITLE" | translate }}</h3>
      <p>{{ "MFA.GUIDE_INTRO" | translate }}</p>
      <ul>
        <li>{{ "MFA.GUIDE_POINT_1" | translate }}</li>
        <li>{{ "MFA.GUIDE_POINT_2" | translate }}</li>
        <li>{{ "MFA.GUIDE_POINT_3" | translate }}</li>
      </ul>
    </div>

    <div class="mfa-app-links">
      <p class="mfa-app-links-title">{{ "MFA.DOWNLOAD_APPS" | translate }}</p>

      <div class="mfa-app-card">
        <div>
          <strong>{{ "MFA.GOOGLE_AUTHENTICATOR" | translate }}</strong>
        </div>
        <div class="mfa-store-links">
          <a
            href="https://play.google.com/store/apps/details?id=com.google.android.apps.authenticator2"
            target="_blank"
            rel="noopener noreferrer"
          >
            {{ "MFA.GOOGLE_PLAY" | translate }}
          </a>
          <a
            href="https://apps.apple.com/app/google-authenticator/id388497605"
            target="_blank"
            rel="noopener noreferrer"
          >
            {{ "MFA.APP_STORE" | translate }}
          </a>
        </div>
      </div>

      <div class="mfa-app-card">
        <div>
          <strong>{{ "MFA.MICROSOFT_AUTHENTICATOR" | translate }}</strong>
        </div>
        <div class="mfa-store-links">
          <a
            href="https://play.google.com/store/apps/details?id=com.azure.authenticator"
            target="_blank"
            rel="noopener noreferrer"
          >
            {{ "MFA.GOOGLE_PLAY" | translate }}
          </a>
          <a
            href="https://apps.apple.com/app/microsoft-authenticator/id983156458"
            target="_blank"
            rel="noopener noreferrer"
          >
            {{ "MFA.APP_STORE" | translate }}
          </a>
        </div>
      </div>
    </div>
  </div>
}

<mat-expansion-panel [expanded]="true" [hideToggle]="dialogMode">
  <mat-expansion-panel-header>
    <mat-panel-title>{{ "MFA.TITLE" | translate }}</mat-panel-title>
  </mat-expansion-panel-header>

  @if (loading) {
    <div class="text-center p-16">
      <mat-spinner diameter="32"></mat-spinner>
    </div>
  }

  @if (!loading && !setupMode && !disableMode) {
    <div class="mfa-status-section">
      <div class="mfa-status">
        <mat-icon [class]="mfaEnabled ? 'text-success' : 'text-warning'">
          {{ mfaEnabled ? "verified_user" : "shield" }}
        </mat-icon>
        <div>
          <p class="status-label">{{ "MFA.STATUS" | translate }}</p>
          <p
            class="status-value"
            [class]="mfaEnabled ? 'text-success' : 'text-warning'"
          >
            {{ (mfaEnabled ? "MFA.ENABLED" : "MFA.DISABLED") | translate }}
          </p>
        </div>
      </div>

      @if (!mfaEnabled) {
        <button mat-raised-button color="primary" (click)="startSetup()">
          {{ "MFA.ENABLE_BUTTON" | translate }}
        </button>
      } @else if (isManagingAnotherUser) {
        <button mat-stroked-button color="warn" (click)="resetManagedUserMfa()">
          {{ "MFA.RESET_BUTTON" | translate }}
        </button>
      } @else {
        <button mat-stroked-button color="warn" (click)="startDisable()">
          {{ "MFA.DISABLE_BUTTON" | translate }}
        </button>
      }
    </div>
  }

  <!-- MFA Setup Mode -->
  @if (setupMode) {
    <div class="mfa-setup-section">
      <h3>{{ "MFA.SETUP_TITLE" | translate }}</h3>
      <p class="setup-instructions">
        @if (isManagingAnotherUser) {
          {{ "MFA.MANAGED_SETUP_INSTRUCTIONS" | translate }}
        } @else {
          {{ "MFA.SETUP_INSTRUCTIONS" | translate }}
        }
      </p>

      <div class="setup-steps">
        <div class="step">
          <span class="step-number">1</span>
          <span>{{ "MFA.STEP_1" | translate }}</span>
        </div>
        <div class="step">
          <span class="step-number">2</span>
          <span>{{ "MFA.STEP_2" | translate }}</span>
        </div>

        @if (qrCodeImage) {
          <div class="qr-code-container">
            <img
              [src]="'data:image/png;base64,' + qrCodeImage"
              alt="QR Code"
              class="qr-code"
            />
          </div>

          <div class="secret-key">
            <p class="secret-label">{{ "MFA.MANUAL_KEY" | translate }}</p>
            <code class="secret-value">{{ secret }}</code>
          </div>
        }

        <div class="step">
          <span class="step-number">3</span>
          <span>{{ "MFA.STEP_3" | translate }}</span>
        </div>
      </div>

      <form [formGroup]="setupForm" (ngSubmit)="verifySetup()">
        <mat-form-field appearance="outline" class="w-100">
          <mat-label>{{ "MFA.ENTER_CODE" | translate }}</mat-label>
          <input
            matInput
            formControlName="code"
            maxlength="6"
            autocomplete="one-time-code"
            style="
              text-align: center;
              font-size: 1.2rem;
              letter-spacing: 0.3rem;
            "
          />
        </mat-form-field>

        <div class="action-buttons">
          <button mat-stroked-button type="button" (click)="cancelSetup()">
            {{ "COMMON.CANCEL" | translate }}
          </button>
          <button
            mat-raised-button
            color="primary"
            type="submit"
            [disabled]="setupForm.invalid"
          >
            {{ "MFA.VERIFY_AND_ENABLE" | translate }}
          </button>
        </div>
      </form>
    </div>
  }

  <!-- MFA Disable Mode -->
  @if (disableMode && !isManagingAnotherUser) {
    <div class="mfa-disable-section">
      <h3>{{ "MFA.DISABLE_TITLE" | translate }}</h3>
      <p class="disable-warning">{{ "MFA.DISABLE_WARNING" | translate }}</p>

      <form [formGroup]="disableForm" (ngSubmit)="confirmDisable()">
        <mat-form-field appearance="outline" class="w-100">
          <mat-label>{{ "MFA.ENTER_CODE" | translate }}</mat-label>
          <input
            matInput
            formControlName="code"
            maxlength="6"
            autocomplete="one-time-code"
            style="
              text-align: center;
              font-size: 1.2rem;
              letter-spacing: 0.3rem;
            "
          />
        </mat-form-field>

        <div class="action-buttons">
          <button mat-stroked-button type="button" (click)="cancelDisable()">
            {{ "COMMON.CANCEL" | translate }}
          </button>
          <button
            mat-raised-button
            color="warn"
            type="submit"
            [disabled]="disableForm.invalid"
          >
            {{ "MFA.CONFIRM_DISABLE" | translate }}
          </button>
        </div>
      </form>
    </div>
  }
</mat-expansion-panel>
